import React, { useEffect, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Icon, IconButton, IconSize, Menu } from 'sidecar-components'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { EnrollmentHelpNav } from './EnrollmentHelpNav'
import { badgeStyles, helpMenuStyles, helpNavStyles } from './HelpNav.styles'
import { getHelpNavItems } from './HelpNav.config'
import { useMemberComms } from 'hooks/useMemberComms'
import { useSidekick } from 'hooks/useSidekick'

/**
 * Menu for page-specific buttons, accessibility controls, inbox, and member
 * care. It's used in the GlobalNav (for desktop) and the PageHeader (for
 * mobile)
 *
 * @param {boolean} authenticated - Whether the user is logged in. Renders
 *   different links if true.
 * @param {JSX.Element | JSX.Element[]} children - List of nav items that go in
 *   between the enrollment buttons and default buttons
 * @param {string} className - Optional classes to add to nav wrapper
 * @param {boolean} hasPolicy - Whether the user has a policy. Renders different
 *   links if true.
 * @param {boolean} isMobile - Whether the nav is being rendered on mobile
 * @param {function} logout - Function to log the user out
 * @param {Sentiment} sentiment - The color of the nav icons
 * @param {number} unresolvedRequests - Number of unresolved requests used to
 *   show the badge on the inbox
 * @returns An HTML nav element
 */
export const HelpNav = ({
  authenticated,
  children,
  className = '',
  hasPolicy,
  isMobile = false,
  logout,
  sentiment = 'default',
  unresolvedRequests = 0,
}) => {
  const flags = useFlags()
  const { openPoptart } = useMemberComms()
  const { openSidekick } = useSidekick()
  const [searchParams] = useSearchParams()
  // Defaulted, not destructured bare: this nav renders inside PageHeader on most
  // pages, and a page test that stubs `useLocation` with only the fields it cares
  // about hands us a location with no pathname. Reading through it crashed four
  // suites. An unknown route falling through to "not on messages" also fails in
  // the right direction — the member keeps an envelope they didn't need, rather
  // than losing the whole nav.
  const { pathname = '' } = useLocation() ?? {}
  const hasHandledAutoOpen = useRef(false)

  // Whether this nav is being drawn inside messages, where the envelope would
  // link to the current page. Matches `/inbox` and the request-detail route
  // under it — a detail view is still "inside messages", and its own back arrow
  // is how the member gets to the list. The separator is required so a future
  // sibling route like `/inboxes` keeps its envelope.
  const isOnMessages = pathname === '/inbox' || pathname.startsWith('/inbox/')

  const showSidekick = flags['sidecar-sidekick'] && authenticated
  const isAshChatGate = flags['ash-chat-gate'] && authenticated
  // When Ash is the front door, the help icon opens it in one tap
  const isAshFrontDoor = flags['ash-human-intercept-enabled'] && authenticated

  // Auto-open Ash when ?ash=true is in URL
  useEffect(() => {
    if (hasHandledAutoOpen.current) return
    if (!showSidekick) return
    if (searchParams.get('ash') !== 'true') return

    hasHandledAutoOpen.current = true
    openSidekick()
    window.heap?.track('ashAutoOpened', { source: 'urlParam' })
  }, [showSidekick, searchParams, openSidekick])

  // include the inbox button (letter icon) if the member is logged in, and isn't
  // already looking at their messages
  const navItems = getHelpNavItems({
    authenticated,
    isAshChatGate,
    isAshFrontDoor,
    isOnMessages,
    openMemberCommPoptart: openPoptart,
    openSidekick,
    showSidekick,
  })

  return (
    <nav className={`${helpNavStyles} ${className}`} data-testid="info_helpNav">
      {/*
      This smaller nav is just a couple of links and is used on the enrollment
      pages because the main nav wouldn't be be available then. It prevents
      users from getting stuck in the sign up flow.
      */}
      {!hasPolicy && (
        <EnrollmentHelpNav logout={logout} authenticated={authenticated} />
      )}

      {/* Children are for page-specific buttons provided within PageHeaders. 
      Ex: bookmark doctor */}
      {children}

      {navItems.map(item => {
        return (
          <div key={item.title} className="relative">
            {item.menuItems ? (
              <Menu
                sections={item.menuItems}
                className={helpMenuStyles}
                onOpenChange={open => {
                  if (open && item.icon === 'tooltip') {
                    window.heap?.track('helpMenuOpened')
                  }
                }}
                trigger={
                  <IconButton
                    label={item.title}
                    // The testId is rendered twice, once for mobile and once
                    // for desktop. This causes a duplicate testId resulting in
                    // failing synthetics. Adding the conditional allows the
                    // synthetics to target the correct testId.
                    testId={isMobile ? `${item.testId}-mobile` : item.testId}
                    icon={item.icon}
                    sentiment={sentiment === 'primary' ? 'brand' : sentiment}
                    {...item.additionalAttributes}
                  />
                }
              />
            ) : (
              <IconButton
                // Same mobile/desktop testId split as the menu branch above so
                // synthetics can target the right one.
                testId={isMobile ? `${item.testId}-mobile` : item.testId}
                icon={item.icon}
                label={item.title}
                onClick={item.onClick}
                path={item.value}
                pathType={item.type}
                sentiment={sentiment === 'primary' ? 'brand' : sentiment}
                {...item.additionalAttributes}
              />
            )}

            {/* Show a notification badge if the item is the messages item and
                showBadge is true. This shows the user that they have a unresolved
                ticket.

                Matched on testId, not title: the title is copy — it read "Inbox"
                until 2026-08-20 and now reads "Messages" behind the front-door flag
                — and a badge that silently stops rendering on a wording change is
                the kind of break nobody notices. */}
            {item.testId === 'globalNav-inbox' && unresolvedRequests > 0 && (
              <Icon
                name="circle"
                className={badgeStyles}
                key={`info_inboxNotificationBadge`}
                size={IconSize.XS}
              />
            )}
          </div>
        )
      })}
    </nav>
  )
}
